<template>
    <div class="product-carousel-container">
        <div v-if="props.headline || props.showMoreLinkText" class="carousel-header">
            <div v-if="props.headline" class="carousel-headline">
                <h2>{{ props.headline }}</h2>
                <p v-if="props.tagline" class="carousel-tagline">{{ props.tagline }}</p>
            </div>
            <div v-if="props.showMoreLinkText" class="carousel-header__action">
                <Link :href="props.showMoreLinkAnchor" class="btn-pc-primary btn-sm">
                    {{ props.showMoreLinkText }}
                    <i class="bi bi-arrow-right ms-1"></i>
                </Link>
            </div>
        </div>

        <div class="carousel-wrapper">
            <!-- Previous button -->
            <button
                v-if="chunkedProducts.length > 1"
                class="carousel-nav carousel-nav--prev"
                type="button"
                @click="prevSlide"
                aria-label="Previous"
            >
                <i class="bi bi-chevron-left"></i>
            </button>

            <!-- Slides -->
            <div class="carousel-track" ref="trackRef">
                <div
                    v-for="(chunk, index) in chunkedProducts"
                    :key="index"
                    class="carousel-slide"
                    :class="{ 'carousel-slide--active': index === activeSlide }"
                >
                    <div class="product-grid" :style="gridStyle">
                        <div v-for="product in chunk" :key="product.slug || product.id" class="product-card">
                            <Link :href="'/produkt/' + product.slug">
                                <div class="product-card__image">
                                    <picture v-if="product.images && product.images.length > 0 && product.images[0].id">
                                        <source :srcset="`/images/${product.images[0].id}?w=320&fmt=webp`" type="image/webp" />
                                        <img
                                            :src="`/images/${product.images[0].id}?w=320`"
                                            :alt="produktnavn(product) || 'Produkt'"
                                            loading="lazy"
                                        />
                                    </picture>
                                    <div v-else class="product-card__placeholder">
                                        <i class="bi bi-image"></i>
                                    </div>
                                </div>
                                <div class="product-card__info">
                                    <h5 class="product-card__title">{{ produktnavn(product) }}</h5>
                                    <div class="product-card__price">
                                        <span class="product-card__price-current">{{ numberFormat(product.prices.lowest_price_including_shipping) }}</span>
                                        <span class="product-card__price-shipping">inkl. fragt</span>
                                    </div>
                                    <div class="product-card__rating" v-if="product.rating">
                                        <span class="product-card__stars">
                                            <i class="bi bi-star-fill" v-for="n in Math.floor(product.rating)" :key="n"></i>
                                            <i class="bi bi-star-half" v-if="product.rating % 1 >= 0.5"></i>
                                        </span>
                                        <span class="product-card__reviews" v-if="product.reviews">{{ product.reviews }}</span>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Next button -->
            <button
                v-if="chunkedProducts.length > 1"
                class="carousel-nav carousel-nav--next"
                type="button"
                @click="nextSlide"
                aria-label="Next"
            >
                <i class="bi bi-chevron-right"></i>
            </button>
        </div>

        <!-- Indicators -->
        <div v-if="chunkedProducts.length > 1" class="carousel-dots">
            <button
                v-for="(_, index) in chunkedProducts"
                :key="index"
                type="button"
                class="carousel-dot"
                :class="{ 'carousel-dot--active': index === activeSlide }"
                @click="goToSlide(index)"
                :aria-label="'Slide ' + (index + 1)"
            />
        </div>
    </div>
</template>

<script setup>
import { produktnavn } from '@/Composables/produktnavn';
    import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
    import { Link } from '@inertiajs/vue3';

    const props = defineProps({
        headline: { type: String, default: null },
        tagline: { type: String, default: '' },
        products: { type: Array, default: () => [] },
        productsPrPage: { type: Number, default: 4 },
        showMoreLinkText: { type: String, default: '' },
        showMoreLinkAnchor: { type: String, default: '' },
        componentIdentifier: { type: String, required: true }
    });

    const activeSlide = ref(0);
    const trackRef = ref(null);
    const visibleCount = ref(props.productsPrPage);
    let autoplayTimer = null;

    function updateVisibleCount() {
        if (typeof window === 'undefined') return;
        const w = window.innerWidth;
        if (w < 576) visibleCount.value = 2;
        else if (w < 768) visibleCount.value = 2;
        else if (w < 992) visibleCount.value = 3;
        else visibleCount.value = props.productsPrPage;
    }

    const gridStyle = computed(() => ({
        'grid-template-columns': `repeat(${visibleCount.value}, 1fr)`
    }));

    const chunkedProducts = computed(() => {
        if (!Array.isArray(props.products) || props.products.length === 0) return [[]];
        const result = [];
        const size = visibleCount.value;
        for (let i = 0; i < props.products.length; i += size) {
            result.push(props.products.slice(i, i + size));
        }
        return result.length > 0 ? result : [[]];
    });

    /*
     * ANTALLET AF SLIDES AENDRER SIG MED VINDUESBREDDEN.
     *
     * Ved under 992 px vises tre varer pr. slide, derover fire. Otte varer er
     * altsaa tre slides i det smalle og to i det brede. Staar autoplay paa den
     * sidste slide naar vinduet goeres bredt igen, peger activeSlide paa en
     * slide der ikke laengere findes — og fordi .carousel-slide er
     * display:none paa alle andre end den aktive, forsvinder HELE karrusellen.
     *
     * Reproduceret 17. september 2026: 1900 px -> 1200 px -> 1900 px gav
     * kortbredde 0 og ét synligt kort i stedet for fire.
     *
     * Der vaages paa antallet frem for at rette i updateVisibleCount, saa det
     * ogsaa holder hvis listen skifter af en anden grund.
     */
    watch(() => chunkedProducts.value.length, (antal) => {
        if (activeSlide.value > antal - 1) {
            activeSlide.value = Math.max(0, antal - 1);
        }
    });

    function nextSlide() {
        activeSlide.value = (activeSlide.value + 1) % chunkedProducts.value.length;
    }

    function prevSlide() {
        activeSlide.value = (activeSlide.value - 1 + chunkedProducts.value.length) % chunkedProducts.value.length;
    }

    function goToSlide(index) {
        activeSlide.value = index;
    }

    function startAutoplay() {
        stopAutoplay();
        if (chunkedProducts.value.length > 1) {
            autoplayTimer = setInterval(nextSlide, 5000);
        }
    }

    function stopAutoplay() {
        if (autoplayTimer) {
            clearInterval(autoplayTimer);
            autoplayTimer = null;
        }
    }

    function numberFormat(value) {
        value = Number(value);
        return new Intl.NumberFormat('da-DK', {
            style: 'currency',
            currency: 'DKK',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    }

    onMounted(() => {
        updateVisibleCount();
        window.addEventListener('resize', updateVisibleCount);
        startAutoplay();
    });

    onUnmounted(() => {
        stopAutoplay();
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', updateVisibleCount);
        }
    });
</script>

<style scoped>
    .product-carousel-container {
        font-family: var(--pc-font);
    }

    /* ── Header ── */
    .carousel-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1rem;
        gap: 1rem;
    }

    .carousel-headline h2 {
        font-size: 1.1rem;
        font-weight: 700;
        text-transform: uppercase;
        color: var(--pc-text-primary);
        margin: 0;
    }

    .carousel-tagline {
        color: var(--pc-text-secondary);
        font-size: 0.9rem;
        margin: 0.25rem 0 0;
    }

    /* ── Carousel wrapper ── */
    .carousel-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    /* ── Navigation arrows ── */
    .carousel-nav {
        flex-shrink: 0;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 1px solid var(--pc-border);
        background: var(--pc-bg-tertiary);
        color: var(--pc-text-primary);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all var(--pc-transition);
        z-index: 2;
        padding: 0;
    }

    .carousel-nav:hover {
        background: var(--pc-accent);
        border-color: var(--pc-accent);
        color: var(--pc-bg-primary);
    }

    /* ── Track ── */
    .carousel-track {
        flex: 1;
        overflow: hidden;
        min-width: 0;
    }

    .carousel-slide {
        display: none;
    }

    .carousel-slide--active {
        display: block;
    }

    /* ── Product grid ── */
    .product-grid {
        display: grid;
        gap: 1rem;
    }

    /* ── Product card ── */
    .product-card {
        background: var(--pc-bg-secondary);
        border: 1px solid var(--pc-border);
        border-radius: var(--pc-radius);
        overflow: hidden;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .product-card:hover {
        transform: translateY(-3px);
        box-shadow: var(--pc-shadow);
        border-color: var(--pc-border-hover);
    }

    .product-card a {
        text-decoration: none;
        color: inherit;
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    .product-card__image {
        height: 160px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        background: #f8f8f8;
    }

    .product-card__image img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }

    .product-card__placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        font-size: 2rem;
        color: var(--pc-text-muted);
    }

    .product-card__info {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    .product-card__title {
        font-size: 0.9rem;
        font-weight: 500;
        margin: 0 0 0.5rem;
        line-height: 1.3;
        color: var(--pc-text-primary);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .product-card__price {
        margin-bottom: 0.5rem;
    }

    .product-card__price-current {
        font-size: 1rem;
        font-weight: 600;
        color: var(--pc-price);
    }

    .product-card__price-shipping {
        color: var(--pc-text-muted);
        font-size: 0.7rem;
        margin-left: 0.25rem;
    }

    .product-card__rating {
        display: flex;
        align-items: center;
        margin-top: auto;
    }

    .product-card__stars {
        color: var(--pc-warning);
        font-size: 0.8rem;
        margin-right: 0.5rem;
    }

    .product-card__reviews {
        color: var(--pc-text-muted);
        font-size: 0.75rem;
    }

    /* ── Dots ── */
    .carousel-dots {
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-top: 1rem;
    }

    .carousel-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 1px solid var(--pc-border);
        background: var(--pc-border-hover);
        padding: 0;
        cursor: pointer;
        opacity: 0.7;
        transition: all 0.2s ease;
    }

    .carousel-dot--active {
        background: var(--pc-accent);
        opacity: 1;
        width: 12px;
        height: 12px;
        box-shadow: 0 0 8px var(--pc-accent-glow);
    }

    /* ── Responsive ── */
    @media (max-width: 767px) {
        .product-card__image {
            height: 120px;
        }

        .product-card__info {
            padding: 0.75rem;
        }

        .product-card__title {
            font-size: 0.8rem;
        }

        .product-card__price-current {
            font-size: 0.9rem;
        }

        .carousel-nav {
            width: 30px;
            height: 30px;
            font-size: 0.85rem;
        }
    }

    @media (max-width: 575px) {
        .product-grid {
            gap: 0.5rem;
        }

        .product-card__image {
            height: 100px;
            padding: 0.5rem;
        }

        .product-card__info {
            padding: 0.5rem;
        }

        .product-card__title {
            font-size: 0.75rem;
            -webkit-line-clamp: 1;
        }

        .product-card__price-current {
            font-size: 0.8rem;
        }

        .product-card__price-shipping {
            display: none;
        }
    }
</style>
